import { useState } from "react";

import { useToast } from "@smart/bridge-intake-components-dom";
import { track } from "@smart/bridge-metrics-dom";
import { Alert, Modal } from "@smart/itops-sb-design-system-dom";
import { pluralise } from "@smart/itops-utils-basic";
import { useUpdateSubmission } from "@smart/manage-gql-client-dom";

import { GqlSubmissionSummary } from "../../types";
import {
  useSubmissionsMatterTypes,
  useSubmissionsWithMatterAccess,
} from "../hooks";

export const BulkCloseFormModal = ({
  submissions,
  onClose,
  onDone,
}: {
  submissions: GqlSubmissionSummary[] | null;
  onClose: () => void;
  onDone?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [updateSubmission] = useUpdateSubmission();
  const { setToasts } = useToast();

  const {
    submissions: submissionsToBeClosed,
    noPermissionsMatterIds,
    loadingMatterPermissions,
  } = useSubmissionsWithMatterAccess(submissions);

  const { leadMatterTypes, matterMatterTypes, loadingMatterTypes } =
    useSubmissionsMatterTypes(submissions);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const matterTypes = [
        ...(leadMatterTypes || []),
        ...(matterMatterTypes || []),
      ];
      await Promise.all(
        submissionsToBeClosed?.map(async (s) => {
          const matterType = matterTypes.find(
            (mt) => mt.id === s.matter?.matterTypeId,
          );

          await updateSubmission({
            variables: {
              uri: s.uri,
              formUri: s.form.uri,
              fields: { status: "cancelled" },
            },
          });

          track("Submission Cancelled", {
            submissionUri: s.uri,
            formUri: s.form.uri,
            matterCategory: s.matter?.isLead ? "lead" : "matter",
            matterTypeId: s.matter?.matterTypeId,
            matterTypeName: matterType?.name,
          });
        }) || [],
      );

      onDone?.();
      setToasts([
        {
          palette: "success",
          text: `${submissionsToBeClosed?.length} ${pluralise(
            submissionsToBeClosed,
            "form",
          )} ${pluralise(submissionsToBeClosed, "was")} successfully closed`,
        },
      ]);
    } catch (error) {
      console.error(error);
      setToasts([
        {
          palette: "destructive",
          text: `${pluralise(
            submissionsToBeClosed,
            "Form",
          )} couldn't be closed. Please try again.`,
        },
      ]);
    }
    setIsLoading(false);
    onClose();
  };

  const loading = isLoading || loadingMatterTypes || loadingMatterPermissions;

  return (
    <Modal
      header={{
        icon: "solidTrashCan",
        iconBgColor: "bg-orange-200",
        text: `Close Forms (${submissions?.length})`,
      }}
      open={!!submissions?.length}
      onClose={onClose}
      footer={{
        buttons: [
          {
            text: "Cancel",
            type: "reset",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            text: "Yes, close",
            onClick: handleSubmit,
            variant: "primaryDefault",
            loading,
          },
        ],
      }}
      size="default"
      closeOptions={{ clickOutside: false }}
      loading={loading}
    >
      {!!noPermissionsMatterIds?.length && (
        <Alert
          content={
            <p>{`You do not have the necessary permissions for ${noPermissionsMatterIds.length} of the selected matters. ${pluralise(noPermissionsMatterIds, "This")} ${pluralise(noPermissionsMatterIds, "form")} will not be closed in this action.`}</p>
          }
          palette="warning"
          variant="section"
        />
      )}
      <div>
        {`Closing ${pluralise(submissionsToBeClosed, "this")} ${pluralise(submissionsToBeClosed, "form")} will prevent the client from entering responses.`}
      </div>
      <div>{`Are you sure you want to close the ${pluralise(submissionsToBeClosed, "form")}?`}</div>
    </Modal>
  );
};
